import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';

const toolName = 'seo-audit-page-settings' as const;

export const auditPageSettingsArgsSchema = z.object({
  pageId: z.string().describe('The ID of the page to audit'),
  siteId: z.string().describe('The ID of the site the page belongs to'),
});

export type AuditPageSettingsArgs = z.infer<typeof auditPageSettingsArgsSchema>;

export const auditPageSettingsResultSchema = z.object({
  pageId: z.string(),
  slug: z.string(),
  hasSeoTitle: z
    .boolean()
    .describe('Whether the page has a non-empty SEO title'),
  hasSeoDesc: z
    .boolean()
    .describe('Whether the page has a non-empty SEO description'),
  hasJsonLdSchema: z
    .boolean()
    .describe('Whether the page has a JSON-LD schema'),
  isSeoTitleTooLong: z
    .boolean()
    .describe('Whether the SEO title exceeds 60 characters'),
  isSeoDescTooLong: z
    .boolean()
    .describe('Whether the SEO description exceeds 160 characters'),
  issueCount: z
    .number()
    .describe(
      'Total number of SEO issues found (missing fields + length violations)'
    ),
  seoTitle: z.string().nullable().describe('Current SEO title or null'),
  seoDesc: z.string().nullable().describe('Current SEO description or null'),
});

export type AuditPageSettingsResult = z.infer<
  typeof auditPageSettingsResultSchema
>;

export const auditPageSettingsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Audit page SEO settings',
  prettyStatus: {
    executed: 'Audited page SEO settings',
    executing: 'Auditing page SEO settings',
    failed: 'Failed to audit page SEO settings',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Checks whether a single page has its SEO settings populated and valid:
      meta title (present and ≤60 characters), meta description (present and
      ≤160 characters), and JSON-LD schema. Returns which fields are missing
      or exceed recommended lengths.
    `,
    inputSchema: auditPageSettingsArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SitePageSettings',
      action: 'read',
      resolveResources: (args) => [{type: 'SitePageSettings', id: args.pageId}],
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 30000,
});
